import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Sailboat, Waves, Sunset, Anchor, Ship, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const formatDistance = (mi, language) =>
  language === 'fr' ? `${(mi * 1.60934).toFixed(1)} km` : `${mi} mi`;

export const WhereToVisit = () => {
  const { t, language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const activities = [
    { icon: Sailboat, title: t('exploreOutdoorsTitle'), desc: t('exploreOutdoorsDesc'),
      links: [
        { label: t('linkFishing'), url: 'https://schoonerwoodwind.com/' },
      ] },
    { icon: Waves, title: t('bikeMontTitle'), desc: t('bikeMontDesc'),
      links: [
        { label: t('linkGoKarting'), url: 'https://capitalsup.com/' },
      ] },
    { icon: Sunset, title: t('natureWalksTitle'), desc: t('natureWalksDesc'),
      links: [
        { label: t('linkKayaking'), url: 'https://tourboatannapolis.com/' },
      ] },
    { icon: Anchor, title: t('tennisTitle'), desc: t('tennisDesc'),
      links: [
        { label: t('linkVeloscenic'), url: 'https://amaritime.org/skipjack-tours/' },
      ] },
    { icon: Ship, title: t('boatShowTitle'), desc: t('boatShowDesc'),
      links: [
        { label: t('linkBoatShow'), url: 'https://www.annapolisboatshows.com/' },
      ] },
  ];

  const dayTrips = [
    { title: t('evreuxTitle'), desc: t('evreuxDesc'), distanceMi: 2.5 },
    { title: t('versaillesTitle'), desc: t('versaillesDesc'), distanceMi: 2 },
    { title: t('verneuiltTitle'), desc: t('verneuilDesc'), distanceMi: 1.5 },
  ];

  return (
    <section ref={ref} className="py-20 bg-[#EFF4F9]" data-testid="explore-section">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }} className="text-center mb-12">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A2B3D] mb-4">{t('whereToVisitTitle')}</h2>
          <div className="wave-divider mb-6" />
          <p className="text-[#6B8299] max-w-2xl mx-auto">{t('whereToVisitDesc')}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}>
          <h3 className="font-serif text-2xl text-[#1A2B3D] mb-6">{t('groundsActivitiesTitle')}</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {activities.map((a) => (
              <div key={a.title} className="bg-white border border-[#CBD5E1] rounded-sm p-5 hover:border-[#1E3A5F] transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <a.icon className="w-4 h-4 text-[#C9A96E]" />
                  <h4 className="font-serif text-lg text-[#1A2B3D]">{a.title}</h4>
                </div>
                <p className="text-[#6B8299] text-sm leading-relaxed mb-3">{a.desc}</p>
                {a.links.length > 0 && (
                  <div className="flex flex-wrap gap-3">
                    {a.links.map((link) => (
                      <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#1E3A5F] text-xs hover:text-[#C9A96E] transition-colors">
                        {link.label} <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}>
          <h3 className="font-serif text-2xl text-[#1A2B3D] mb-6">{t('dayTripsTitle')}</h3>
          <div className="space-y-4">
            {dayTrips.map((trip) => (
              <div key={trip.title} className="bg-white border border-[#CBD5E1] rounded-sm p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h4 className="font-serif text-lg text-[#1A2B3D] mb-1">{trip.title}</h4>
                  <p className="text-[#6B8299] text-sm">{trip.desc}</p>
                </div>
                <span className="flex items-center gap-1 text-[#1E3A5F] text-sm whitespace-nowrap">
                  <MapPin className="w-3 h-3" /> {formatDistance(trip.distanceMi, language)} {t('fromVenue')}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
